import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
} from 'react-native';
import { useResponsive, useRoleNavigation, useAutoTranslate } from '../../hooks';
import { useRoute, useNavigation, RouteProp, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path, Circle } from 'react-native-svg';
import { activitiesApi, kpisApi, activityCommentsApi, ActivityComment } from '../../services/apiService';
import { Activity, KPIList } from '../../types';
import { formatPct } from '../../utils/formatPercentage';
import DeletionImpactModal from '../../components/DeletionImpactModal';
import DeletionApprovalBanner from '../../components/DeletionApprovalBanner';
import { COLORS, FONTS, LABELS } from '../../constants/config';
import {
  LoadingScreen,
  EmptyState,
  PageContainer,
  Navbar,
  Breadcrumb,
  CommentSection,
  CommentData,
  getAvatarColorFromUserType,
  UserType,
} from '../../components';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useYear } from '../../contexts/YearContext';
import { useNotification } from '../../contexts/NotificationContext';

type RouteProps = RouteProp<MainStackParamList, 'ActivityDetail'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icon Components
// ============================================================

const ChevronLeftIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const EditIcon = ({ size = 20, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 3H5C4.46957 3 3.96086 3.21071 3.58579 3.58579C3.21071 3.96086 3 4.46957 3 5V19C3 19.5304 3.21071 20.0391 3.58579 20.4142C3.96086 20.7893 4.46957 21 5 21H19C19.5304 21 20.0391 20.7893 20.4142 20.4142C20.7893 20.0391 21 19.5304 21 19V12" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M18.3751 2.62523C18.7729 2.2274 19.3125 2.00391 19.8751 2.00391C20.4377 2.00391 20.9773 2.2274 21.3751 2.62523C21.7729 3.02305 21.9964 3.56262 21.9964 4.12523C21.9964 4.68784 21.7729 5.2274 21.3751 5.62523L12.3621 14.6392C12.1246 14.8765 11.8313 15.0501 11.5091 15.1442L8.63609 15.9842C8.55005 16.0093 8.45883 16.0108 8.372 15.9886C8.28517 15.9663 8.20592 15.9212 8.14254 15.8578C8.07916 15.7944 8.03398 15.7151 8.01174 15.6283C7.98949 15.5415 7.991 15.4503 8.01609 15.3642L8.85609 12.4912C8.95062 12.1693 9.12463 11.8763 9.36209 11.6392L18.3751 2.62523Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const PlusIcon = ({ size = 16, color = '#FFFFFF' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Donut Chart Component for Status Distribution
// ============================================================

interface PieChartProps {
  excellent: number;
  good: number;
  needsImprovement: number;
  size?: number;
}

const PieChart = ({ excellent, good, needsImprovement, size = 120 }: PieChartProps) => {
  const total = excellent + good + needsImprovement || 1;
  const center = size / 2;
  const outerRadius = size / 2 - 5;
  const innerRadius = outerRadius * 0.6;
  const strokeWidth = outerRadius - innerRadius;
  const radius = (outerRadius + innerRadius) / 2;
  const circumference = 2 * Math.PI * radius;

  const excellentPercent = excellent / total;
  const goodPercent = good / total;
  const needsImprovementPercent = needsImprovement / total;

  const excellentDash = circumference * excellentPercent;
  const goodDash = circumference * goodPercent;
  const needsImprovementDash = circumference * needsImprovementPercent;

  const excellentRotation = -90;
  const goodRotation = excellentRotation + (excellentPercent * 360);
  const needsImprovementRotation = goodRotation + (goodPercent * 360);

  return (
    <Svg width={size} height={size}>
      {excellent > 0 && (
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke="#2CA02C"
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={`${excellentDash} ${circumference}`}
          strokeDashoffset={0}
          rotation={excellentRotation}
          origin={`${center}, ${center}`}
        />
      )}
      {good > 0 && (
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke="#FF7F0E"
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={`${goodDash} ${circumference}`}
          strokeDashoffset={0}
          rotation={goodRotation}
          origin={`${center}, ${center}`}
        />
      )}
      {needsImprovement > 0 && (
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke="#D62728"
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={`${needsImprovementDash} ${circumference}`}
          strokeDashoffset={0}
          rotation={needsImprovementRotation}
          origin={`${center}, ${center}`}
        />
      )}
    </Svg>
  );
};

// ============================================================
// Circular Gauge Component
// ============================================================

interface CircularGaugeProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
  backgroundColor?: string;
}

const CircularGauge = ({
  percentage,
  size = 120,
  strokeWidth = 12,
  color = '#2CA02C',
  backgroundColor = '#E5E7EB'
}: CircularGaugeProps) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const center = size / 2;

  // Calculate dot position at END of progress arc
  // -90 offset to start from top (12 o'clock position)
  const angle = ((percentage / 100) * 360 - 90) * (Math.PI / 180);
  const dotX = center + radius * Math.cos(angle);
  const dotY = center + radius * Math.sin(angle);

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size}>
        {/* Background circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={backgroundColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress arc - rotation applied to this element only */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
        {/* Dot at end of progress - ONLY show if percentage > 0 */}
        {percentage > 0 && (
          <Circle
            cx={dotX}
            cy={dotY}
            r={strokeWidth / 2 + 2}
            fill={color}
            stroke="#FCFCFC"
            strokeWidth={2}
          />
        )}
      </Svg>
      <View style={{ position: 'absolute', alignItems: 'center' }}>
        <Text style={{ fontSize: size > 100 ? 24 : 16, fontFamily: FONTS.numbers, fontWeight: '500', color: '#414042' }}>
          %{percentage}
        </Text>
      </View>
    </View>
  );
};

// ============================================================
// Main Screen
// ============================================================

export default function ActivityDetailScreen() {
  const route = useRoute<RouteProps>();
  const navigation = useNavigation<NavigationProp>();
  const { user } = useAuth();
  const { id } = route.params;
  const { isMobile, isTablet, isDesktop, isSmallMobile, horizontalCardWidth, containerPadding, cardPadding, sectionSpacing } = useResponsive();
  const { navigateToHome, isViewOnly } = useRoleNavigation();
  const { showSuccess, showError } = useNotification();
  // DTS 2.2: only Strategy Team (hrcpms.soft_delete) can delete operational
  // items. Section Head has create/edit; delete is Strategy Team's only.
  const canSoftDelete = !!(
    user?.is_superuser
    || (user?.permissions || []).includes('hrcpms.soft_delete')
  );
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();
  const { translate, translateOrg } = useAutoTranslate();
  const { selectedYear } = useYear();

  // Theme colors for dark mode
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textSecondary: colors.textSecondary,
    textMuted: colors.textMuted,
    iconDefault: colors.iconDefault,
    border: colors.border,
    backgroundAlt: colors.backgroundAlt,
    sectionBg: isDark ? colors.backgroundAlt : '#F1F5F9',
  };

  const [activity, setActivity] = useState<Activity | null>(null);
  const [kpis, setKpis] = useState<KPIList[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Comments state
  const [comments, setComments] = useState<CommentData[]>([]);
  const [showAddComment, setShowAddComment] = useState(false);
  const [editingComment, setEditingComment] = useState<CommentData | null>(null);

  const isOrgEmployee = user?.position_type === 'EMPLOYEE';
  const [showDeletionImpact, setShowDeletionImpact] = useState(false);
  const [isActivityDeleting, setIsActivityDeleting] = useState(false);
  const canApproveDeletion = (user?.is_superuser || user?.position_type === 'MANAGER' || user?.position_type === 'DIRECTOR' || (user?.permissions?.includes('hrcpms.strategic_goals.create') ?? false));

  // Convert API comment to CommentData format for display (same as InitiativeDetailScreen)
  const convertApiComment = (apiComment: ActivityComment): CommentData => {
    const fullName = apiComment.author_details?.full_name || apiComment.author_details?.username || t('common.user');
    const nameParts = fullName.split(' ').filter(Boolean);
    const authorName = nameParts[0] || t('common.user'); // First name only

    const initials = nameParts.length >= 2
      ? `${nameParts[0][0]} ${nameParts[1][0]}`
      : fullName.slice(0, 2);

    const isOwner = user?.id === apiComment.author;
    const userType = (apiComment.author_type || 'org_employee') as UserType;
    const avatarColor = getAvatarColorFromUserType(userType);

    return {
      id: apiComment.id,
      name: authorName,
      initials,
      date: new Date(apiComment.created_at).toLocaleDateString('ar-SA', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
      text: apiComment.content,
      avatarColor,
      showMenu: isOwner, // Only show menu for own comments
      isEdited: apiComment.is_edited,
      originalText: apiComment.original_content,
      userType,
    };
  };

  const fetchComments = useCallback(async () => {
    try {
      const commentsData = await activityCommentsApi.getForActivity(id);
      setComments(commentsData.map(convertApiComment));
    } catch {
      setComments([]);
    }
  }, [id, user?.id]);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const data = await activitiesApi.get(id);
      setActivity(data);

      // Fetch KPIs for this activity
      try {
        console.log('Fetching KPIs for activity:', id);
        const kpisData = await kpisApi.list({ source_activity: id, page_size: 100 });
        console.log('KPIs response:', kpisData);
        console.log('KPIs results:', kpisData.results);
        setKpis(kpisData.results || []);
      } catch (kpiError) {
        console.error('Error fetching KPIs:', kpiError);
        setKpis([]);
      }

      // Fetch comments for this activity
      await fetchComments();
    } catch (err: any) {
      setError(err.message || t('messages.error.loadFailed'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [id, fetchComments]);

  // Refetch data when screen comes into focus (handles initial load and returning from other screens)
  useFocusEffect(
    useCallback(() => {
      console.log('ActivityDetailScreen focused - refetching data for activity:', id);
      fetchData();
    }, [fetchData])
  );

  // Refetch when year changes
  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleEditActivity = () => {
    navigation.navigate('AddActivity', { initiativeId: activity?.initiative || 0, activityId: id });
  };

  const handleAddKPI = () => {
    navigation.navigate('AddActivity', { initiativeId: activity?.initiative || 0, activityId: id, scrollToKpi: true });
  };

  const handleViewKPIDetails = (kpiId: number) => {
    navigation.navigate('KPIDetail', { id: kpiId });
  };

  // Comment handlers
  const handleAddComment = async (text: string) => {
    try {
      // Determine author_type based on user permissions
      const authorType = (user?.permissions?.includes('hrcpms.strategic_goals.create') ?? false) ? 'strategy_team' : 'org_employee';
      await activityCommentsApi.create({
        activity: id,
        content: text,
        comment_type: 'note',
        author_type: authorType,
      });
      await fetchComments();
    } catch (err: any) {
      showError(err.message || t('messages.error.saveFailed'));
    } finally {
      setShowAddComment(false);
    }
  };

  const handleEditComment = (comment: CommentData) => {
    setEditingComment(comment);
    setShowAddComment(true);
  };

  const handleUpdateComment = async (text: string) => {
    try {
      if (editingComment) {
        await activityCommentsApi.update(editingComment.id, { content: text });
        await fetchComments();
      }
    } catch (err: any) {
      showError(err.message || t('messages.error.saveFailed'));
    } finally {
      setEditingComment(null);
      setShowAddComment(false);
    }
  };

  const handleDeleteActivity = () => { setShowDeletionImpact(true); };
  const confirmDeleteActivity = async () => {
    try {
      setIsActivityDeleting(true);
      const response = await activitiesApi.delete(id);
      if ((response as any)?.status === 202) { showSuccess(t('deletion.requestSent')); }
      else { showSuccess(t('activities.deleteSuccess')); }
      navigation.goBack();
    } catch (error: any) {
      if (error.response?.status === 202) { showSuccess(t('deletion.requestSent')); navigation.goBack(); }
      else if (error.response?.status === 409) { showError(t('deletion.alreadyPending')); }
      else { showError(t('messages.deleteFailed')); }
    } finally { setIsActivityDeleting(false); setShowDeletionImpact(false); }
  };

  const handleDeleteComment = async (commentId: number) => {
    try {
      const response = await activityCommentsApi.delete(commentId);
      if ((response as any)?.status === 202) {
        showSuccess(t('deletion.requestSent'));
      }
      await fetchComments();
    } catch (err: any) {
      if (err.response?.status === 202) {
        showSuccess(t('deletion.requestSent'));
        await fetchComments();
      } else {
        showError(err.message || t('messages.deleteFailed'));
      }
    }
  };

  const handleCancelComment = () => {
    setShowAddComment(false);
    setEditingComment(null);
  };

  const handleCommentSubmit = (text: string) => {
    if (editingComment) {
      handleUpdateComment(text);
    } else {
      handleAddComment(text);
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (error || !activity) {
    return (
      <EmptyState
        icon="alert-circle-outline"
        title={LABELS.error}
        message={error || t('common.noData')}
        actionLabel={LABELS.refresh}
        onAction={fetchData}
      />
    );
  }

  // Calculate progress based on activity dates
  const calculateProgress = (): number => {
    if (!activity.start_date || !activity.end_date) return 0;
    const startDate = new Date(activity.start_date);
    const endDate = new Date(activity.end_date);
    const today = new Date();
    if (today < startDate) return 0;
    if (today >= endDate) return 100;
    const totalDuration = endDate.getTime() - startDate.getTime();
    const elapsed = today.getTime() - startDate.getTime();
    return Math.min(Math.round((elapsed / totalDuration) * 100), 100);
  };

  const activityProgress = calculateProgress();

  // Calculate status distribution from actual KPI data
  const calculateStatusDistribution = () => {
    if (kpis.length === 0) {
      return { excellent: 0, good: 0, needsImprovement: 0 };
    }
    let excellent = 0;
    let good = 0;
    let needsImprovement = 0;

    kpis.forEach((kpi) => {
      const achievement = kpi.achievement_percentage || 0;
      if (achievement >= 80) {
        excellent++;
      } else if (achievement >= 50) {
        good++;
      } else {
        needsImprovement++;
      }
    });

    const total = kpis.length;
    return {
      excellent: parseFloat(((excellent / total) * 100).toFixed(2)),
      good: parseFloat(((good / total) * 100).toFixed(2)),
      needsImprovement: parseFloat(((needsImprovement / total) * 100).toFixed(2)),
    };
  };

  const statusDistribution = calculateStatusDistribution();
  const excellentPercent = statusDistribution.excellent;
  const goodPercent = statusDistribution.good;
  const needsImprovementPercent = statusDistribution.needsImprovement;

  // Calculate average KPI achievement percentage
  // null when no KPIs — shows "--" instead of 0%
  const averageKpiAchievement = kpis.length > 0
    ? parseFloat((kpis.reduce((sum, kpi) => sum + (kpi.achievement_percentage || 0), 0) / kpis.length).toFixed(2))
    : null;

  // Format date for display
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ar-SA', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Format budget for display
  const formatBudget = (budget: string) => {
    if (!budget || budget === '0' || budget === '0.00') return '-';
    const num = parseFloat(budget);
    return `${num.toLocaleString('ar-SA')} ${t('common.currency')}`;
  };

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[
          styles.content,
          { padding: containerPadding }
        ]}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('navigation.home'), route: 'Dashboard' },
            { label: t('navigation.initiatives'), route: 'Initiatives' },
            { label: t('initiatives.activityDetails') },
          ]}
        />

        {/* Title Section */}
        <View style={[styles.titleSectionCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            {canSoftDelete && (
              <TouchableOpacity style={[styles.editIconButton, { minHeight: 44, minWidth: 44, backgroundColor: '#FEE2E2', borderColor: '#FECACA', borderWidth: 1 }]} onPress={handleDeleteActivity}>
                <Text style={{ fontSize: 12, color: '#DC2626', fontFamily: FONTS.medium }}>{t('common.delete')}</Text>
              </TouchableOpacity>
            )}
            {!isViewOnly && (
              <TouchableOpacity style={[styles.editIconButton, { minHeight: 44, minWidth: 44 }]} onPress={handleEditActivity}>
                <EditIcon size={15} color={themeColors.iconDefault} />
              </TouchableOpacity>
            )}
          </View>
          <View style={styles.titleTextContainer}>
            <Text style={[styles.pageTitle, { color: themeColors.textPrimary }]}>{translate(activity.name)}</Text>
            {activity.description && (
              <Text style={[styles.pageDescription, { color: themeColors.textMuted }]}>
                {translate(activity.description)}
              </Text>
            )}
          </View>
        </View>

        {(activity as any).pending_deletion_info && (
          <DeletionApprovalBanner
            deletionInfo={(activity as any).pending_deletion_info}
            canApprove={canApproveDeletion}
            canCancel={isOrgEmployee && (activity as any).pending_deletion_info.requested_by_id === user?.id}
            onApproved={() => { showSuccess(t('deletion.approved')); navigation.goBack(); }}
            onRejected={() => { showSuccess(t('deletion.rejected')); fetchData(); }}
            onCancelled={() => { showSuccess(t('deletion.requestCancelled')); fetchData(); }}
            onViewImpact={() => setShowDeletionImpact(true)}
          />
        )}
        {/* Info Cards Row */}
        <View style={[
          styles.infoCardsRow,
          isMobile && { flexDirection: 'row', flexWrap: 'wrap', gap: 12 }
        ]}>
          <View style={[styles.infoCard, isMobile && { minWidth: '45%', flex: 1 }]}>
            <Text style={styles.infoCardLabel}>{t('initiatives.endDate')}</Text>
            <Text style={styles.infoCardValue}>{formatDate(activity.end_date)}</Text>
          </View>
          <View style={[styles.infoCard, isMobile && { minWidth: '45%', flex: 1 }]}>
            <Text style={styles.infoCardLabel}>{t('initiatives.startDate')}</Text>
            <Text style={styles.infoCardValue}>{formatDate(activity.start_date)}</Text>
          </View>
          <View style={[styles.infoCard, isMobile && { minWidth: '45%', flex: 1 }]}>
            <Text style={styles.infoCardLabel}>{t('initiatives.responsibleUnit')}</Text>
            <Text style={styles.infoCardValue}>{activity.responsible_unit_names?.length > 0 ? activity.responsible_unit_names.map((n: string) => translate(n)).join('، ') : '-'}</Text>
          </View>
          <View style={[styles.infoCard, isMobile && { minWidth: '45%', flex: 1 }]}>
            <Text style={styles.infoCardLabel}>{t('initiatives.linkedInitiative')}</Text>
            <Text style={styles.infoCardValue}>{translate(activity.initiative_title) || '-'}</Text>
          </View>
        </View>

        {/* Middle Section: Status Distribution + KPIs Gauge + Activity Details */}
        <View style={[
          styles.middleSectionRow,
          isMobile && { flexDirection: 'column', gap: 16 }
        ]}>
          {/* Status Distribution Card - Left */}
          <View style={[styles.statusDistributionCard, isMobile && { flex: 0, width: '100%' }]}>
            <Text style={styles.cardTitleRight}>{t('kpis.statusDistribution')}</Text>
            <View style={styles.statusDistributionContent}>
              <View style={styles.donutChartWrapper}>
                <PieChart
                  excellent={excellentPercent}
                  good={goodPercent}
                  needsImprovement={needsImprovementPercent}
                  size={120}
                />
              </View>
              <View style={styles.statusLegend}>
                <View style={styles.statusLegendItem}>
                  <Text style={styles.statusLegendPercent}>% {excellentPercent}</Text>
                  <View style={styles.statusLegendLabelRow}>
                    <Text style={styles.statusLegendLabel}>{t('status.excellent')}</Text>
                    <View style={[styles.statusLegendBar, { backgroundColor: '#2CA02C' }]} />
                  </View>
                </View>
                <View style={styles.statusLegendItem}>
                  <Text style={styles.statusLegendPercent}>% {goodPercent}</Text>
                  <View style={styles.statusLegendLabelRow}>
                    <Text style={styles.statusLegendLabel}>{t('status.good')}</Text>
                    <View style={[styles.statusLegendBar, { backgroundColor: '#FF7F0E' }]} />
                  </View>
                </View>
                <View style={styles.statusLegendItem}>
                  <Text style={styles.statusLegendPercent}>% {needsImprovementPercent}</Text>
                  <View style={styles.statusLegendLabelRow}>
                    <Text style={styles.statusLegendLabel}>{t('status.needsImprovement')}</Text>
                    <View style={[styles.statusLegendBar, { backgroundColor: '#D62728' }]} />
                  </View>
                </View>
              </View>
            </View>
          </View>

          {/* KPIs Performance Gauge - Center */}
          <View style={[styles.gaugeCard, isMobile && { flex: 0, width: '100%' }]}>
            <Text style={styles.cardTitleRight}>{t('kpis.performanceIndicators')}</Text>
            {averageKpiAchievement != null ? (
              <View style={styles.gaugeContainer}>
                <CircularGauge
                  percentage={averageKpiAchievement}
                  size={isMobile ? 120 : 144}
                  strokeWidth={12}
                  color={averageKpiAchievement >= 80 ? '#2CA02C' : averageKpiAchievement >= 50 ? '#FF7F0E' : '#D62728'}
                />
              </View>
            ) : (
              <View style={[styles.gaugeContainer, { justifyContent: 'center', alignItems: 'center', minHeight: isMobile ? 120 : 144 }]}>
                <Text style={{ fontSize: 36, fontWeight: '700', color: '#9CA3AF' }}>--</Text>
              </View>
            )}
          </View>

          {/* Activity Details Card - Right */}
          <View style={[styles.activityDetailsCard, isMobile && { flex: 0, width: '100%' }]}>
            <Text style={styles.cardTitleRight}>{t('initiatives.activityDetails')}</Text>
            <View style={styles.detailsGrid}>
              <View style={styles.detailRow}>
                <Text style={styles.detailValue}>{formatBudget(activity.budget)}</Text>
                <Text style={styles.detailLabel}>{t('initiatives.budget')}</Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailValue}>{translate(activity.human_resources) || '-'}</Text>
                <Text style={styles.detailLabel}>{t('initiatives.resources')}</Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailValue}>{translate(activity.technical_resources) || '-'}</Text>
                <Text style={styles.detailLabel}>{t('initiatives.technicalResources')}</Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailValue}>{translate(activity.partners) || '-'}</Text>
                <Text style={styles.detailLabel}>{t('initiatives.partners')}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Bottom Section: Activity Links + Comments */}
        <View style={[
          styles.bottomSectionGrid,
          isMobile && { flexDirection: 'column', gap: 16 }
        ]}>
          {/* Activity Links */}
          <View style={[styles.linksColumnFigma, isMobile && { flex: 0, width: '100%' }]}>
            <View style={styles.linksCardContainer}>
              <Text style={styles.linksCardTitle}>{t('initiatives.activityLinks')}</Text>

              {/* KPIs Section */}
              <View style={styles.linksSectionGray}>
                <View style={styles.linksSectionHeaderRow}>
                  <TouchableOpacity style={[styles.addKpiButton, { minHeight: 44 }]} onPress={handleAddKPI}>
                    <PlusIcon size={14} color="#FFFFFF" />
                    <Text style={styles.addKpiButtonText}>{t('kpis.addIndicator')}</Text>
                  </TouchableOpacity>
                  <Text style={styles.linksSectionTitleText}>{t('kpis.performanceIndicators')} ({kpis.length})</Text>
                </View>

                <View style={styles.rtlWrapper}>
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={true}
                    contentContainerStyle={styles.horizontalCardsRow}
                    style={styles.horizontalScrollContainer}
                  >
                    {kpis.length > 0 ? (
                      kpis.map((kpi) => {
                        const achievement = kpi.achievement_percentage || 0;
                        const progressColor = achievement >= 100 ? '#22C55E' : achievement >= 80 ? '#F97316' : '#EF4444';
                        return (
                          <View key={kpi.id} style={[styles.rtlCard, styles.kpiCard, { width: horizontalCardWidth }]}>
                            <Text style={styles.kpiCardTitle}>{translate(kpi.name)}</Text>
                            {/* Weight badge */}
                            {kpi.weight && (
                              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 6, marginBottom: 4 }}>
                                <Text style={{ fontSize: 12, fontFamily: FONTS.regular, color: '#6B7280' }}>{t('common.weight')}</Text>
                                <View style={{ backgroundColor: '#F1F5F9', paddingHorizontal: 8, paddingVertical: 2, borderRadius: 4 }}>
                                  <Text style={{ fontSize: 12, fontFamily: FONTS.numbers, fontWeight: '500', color: '#414042' }}>% {kpi.weight}</Text>
                                </View>
                              </View>
                            )}

                            {/* Progress Row */}
                            <View style={styles.kpiProgressRow}>
                              <View style={styles.kpiValueBadge}>
                                <Text style={styles.kpiValue}>% {achievement}</Text>
                              </View>
                              <View style={styles.kpiProgressContainer}>
                                <View style={styles.kpiProgressTrack}>
                                  <View
                                    style={[
                                      styles.kpiProgressFill,
                                      {
                                        width: `${Math.min(achievement, 100)}%`,
                                        backgroundColor: progressColor
                                      }
                                    ]}
                                  />
                                  <View
                                    style={[
                                      styles.kpiProgressDot,
                                      {
                                        backgroundColor: progressColor,
                                        left: `${100 - Math.min(achievement, 100)}%`,
                                        transform: [{ translateX: -6 }]
                                      }
                                    ]}
                                  />
                                </View>
                              </View>
                            </View>

                            <TouchableOpacity
                              style={styles.kpiDetailsLink}
                              onPress={() => handleViewKPIDetails(kpi.id)}
                            >
                              <ChevronLeftIcon size={16} color="#414042" />
                              <Text style={styles.kpiDetailsText}>{t('common.viewDetails')}</Text>
                            </TouchableOpacity>
                          </View>
                        );
                      })
                    ) : (
                      <View style={[styles.emptyStateContainer, styles.rtlCard]}>
                        <Text style={styles.emptyStateText}>{t('initiatives.noLinkedKPIs')}</Text>
                      </View>
                    )}
                  </ScrollView>
                </View>
              </View>
            </View>
          </View>

          {/* Comments Section */}
          <View style={[styles.commentsColumn, isMobile && { flex: 0, width: '100%' }]}>
            <CommentSection
              title={t('comments.title')}
              comments={comments}
              showAddComment={showAddComment}
              editingComment={editingComment}
              onAddClick={() => setShowAddComment(true)}
              onEditComment={handleEditComment}
              onDeleteComment={handleDeleteComment}
              onSubmitComment={handleCommentSubmit}
              onCancelComment={handleCancelComment}
            />
          </View>
        </View>
      </ScrollView>
      <DeletionImpactModal
        visible={showDeletionImpact}
        onClose={() => setShowDeletionImpact(false)}
        onConfirm={confirmDeleteActivity}
        entityName={activity?.name || ''}
        fetchImpact={async () => { const r = await activitiesApi.deletionImpact(id); return r.data; }}
        isOrgEmployee={isOrgEmployee}
        isSubmitting={isActivityDeleting}
      />
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 20,
  },
  // Breadcrumb
  breadcrumbRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: 16,
    gap: 8,
  },
  breadcrumbLink: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  breadcrumbSeparator: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  breadcrumbCurrent: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  // Title Section Card
  titleSectionCard: {
    backgroundColor: COLORS.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 20,
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    gap: 8,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  editIconButton: {
    backgroundColor: 'rgba(51, 65, 85, 0.05)',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    minWidth: 32,
    minHeight: 32,
    padding: 7,
    alignItems: 'center',
    justifyContent: 'center',
  },
  titleTextContainer: {
    flex: 1,
    gap: 8,
    alignItems: 'flex-end',
    flexShrink: 1,
    minWidth: 0,
  },
  pageTitle: {
    fontSize: 20,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 26,
    width: '100%',
  },
  pageDescription: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    color: '#64748B',
    textAlign: 'right',
    lineHeight: 24,
    width: '100%',
    flexShrink: 1,
  },
  // Info Cards Row
  infoCardsRow: {
    flexDirection: 'row',
    gap: 20,
    marginBottom: 20,
  },
  infoCard: {
    flex: 1,
    backgroundColor: COLORS.white,
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    alignItems: 'flex-end',
    gap: 4,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  infoCardLabel: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 20,
  },
  infoCardValue: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#64748B',
    textAlign: 'right',
    lineHeight: 14,
  },
  // Middle Section Row
  middleSectionRow: {
    flexDirection: 'row',
    gap: 20,
    marginBottom: 20,
  },
  // Status Distribution Card
  statusDistributionCard: {
    flex: 1,
    backgroundColor: COLORS.white,
    borderRadius: 8,
    padding: 20,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    gap: 20,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  statusDistributionContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  donutChartWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  statusLegend: {
    gap: 24,
  },
  statusLegendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  statusLegendPercent: {
    fontSize: 20,
    fontFamily: FONTS.medium,
    color: '#303030',
    textAlign: 'right',
  },
  statusLegendLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 4,
  },
  statusLegendLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#64748B',
    width: 72,
    textAlign: 'right',
  },
  statusLegendBar: {
    width: 8,
    height: 32,
    borderRadius: 2,
  },
  // Gauge Card
  gaugeCard: {
    flex: 1,
    backgroundColor: COLORS.white,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 20,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 20,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  cardTitleRight: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    width: '100%',
    lineHeight: 24,
  },
  gaugeContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Activity Details Card
  activityDetailsCard: {
    flex: 1,
    backgroundColor: COLORS.white,
    borderRadius: 8,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    gap: 16,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  detailsGrid: {
    gap: 12,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  detailLabel: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    minWidth: 100,
  },
  detailValue: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#64748B',
    textAlign: 'right',
    flex: 1,
  },
  // Bottom Section
  bottomSectionGrid: {
    flexDirection: 'row-reverse',
    gap: 20,
  },
  linksColumnFigma: {
    flex: 2,
  },
  linksCardContainer: {
    backgroundColor: COLORS.white,
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    gap: 16,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  linksCardTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 20,
  },
  linksSectionGray: {
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    padding: 12,
    gap: 10,
  },
  linksSectionTitleText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 20,
    flex: 1,
  },
  linksSectionHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  commentsColumn: {
    flex: 1,
  },
  // Empty State
  emptyStateContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 24,
    minWidth: '100%',
  },
  emptyStateText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#9CA3AF',
    textAlign: 'center',
  },
  // Goal Cards Grid
  goalCardsGrid: {
    flexDirection: 'row',
    gap: 20,
    flexWrap: 'wrap',
  },
  // Goal Card - matching KPI card style from Figma
  goalCard: {
    backgroundColor: COLORS.white,
    borderRadius: 8,
    padding: 12,
    flex: 1,
    minWidth: 200,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    gap: 16,
  },
  goalCardTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 20,
  },
  goalProgressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
  },
  goalValueBadge: {
    backgroundColor: '#F1F5F9',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 100,
  },
  goalValue: {
    fontSize: 14,
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: '#414042',
  },
  goalProgressContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  goalProgressTrack: {
    height: 8,
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    position: 'relative',
    overflow: 'visible',
  },
  goalProgressFill: {
    position: 'absolute',
    right: 0,
    top: 0,
    height: '100%',
    borderRadius: 8,
  },
  goalProgressDot: {
    position: 'absolute',
    width: 14,
    height: 14,
    borderRadius: 7,
    top: -3,
    borderWidth: 2,
    borderColor: COLORS.white,
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.2)',
    elevation: 2,
  },
  goalDetailsLink: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 8,
    paddingHorizontal: 8,
    paddingVertical: 3,
    minHeight: 24,
  },
  goalDetailsText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  // KPI Section Styles
  addKpiButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#A9211F',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    gap: 6,
  },
  addKpiButtonText: {
    fontSize: 12,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
  // Horizontal scroll container for cards
  horizontalScrollContainer: {
    flexGrow: 0,
  },
  // RTL wrapper - flips the entire ScrollView horizontally so cards start from right
  rtlWrapper: {
    transform: [{ scaleX: -1 }],
  },
  // RTL card - flips each card back to normal orientation
  rtlCard: {
    transform: [{ scaleX: -1 }],
  },
  horizontalCardsRow: {
    flexDirection: 'row',
    gap: 16,
    paddingHorizontal: 4,
    paddingBottom: 12,
  },
  kpiCard: {
    backgroundColor: COLORS.white,
    borderRadius: 8,
    padding: 12,
    width: 288, // Fixed width (w-72)
    flexShrink: 0, // Prevent shrinking
    borderWidth: 1,
    borderColor: '#E2E8F0',
    gap: 16,
  },
  kpiCardTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 20,
  },
  kpiProgressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
  },
  kpiValueBadge: {
    backgroundColor: '#F1F5F9',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 100,
  },
  kpiValue: {
    fontSize: 14,
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: '#414042',
  },
  kpiProgressContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  kpiProgressTrack: {
    height: 8,
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    position: 'relative',
    overflow: 'visible',
  },
  kpiProgressFill: {
    position: 'absolute',
    right: 0,
    top: 0,
    height: '100%',
    borderRadius: 8,
  },
  kpiProgressDot: {
    position: 'absolute',
    width: 14,
    height: 14,
    borderRadius: 7,
    top: -3,
    borderWidth: 2,
    borderColor: COLORS.white,
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.2)',
    elevation: 2,
  },
  kpiDetailsLink: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 8,
    paddingHorizontal: 8,
    paddingVertical: 3,
    minHeight: 24,
  },
  kpiDetailsText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
});
